import { useEffect, useRef, useState } from "react";

/**
 * Tracks whether form values have changed from their initial state.
 * @param {any} currentValues - current form state (object, array, or primitive)
 * @param {any} [resetKey] - when this changes, the initial snapshot is recaptured
 *   (e.g. pass `open` for dialogs so dirty resets each time the dialog opens)
 */
export const useFormDirty = (currentValues, resetKey) => {
  const [locked, setLocked] = useState(false);
  const initialRef = useRef(null);
  const keyRef = useRef(resetKey);
  const rafRef = useRef(null);

  const serialized = JSON.stringify(currentValues);

  if (keyRef.current !== resetKey) {
    keyRef.current = resetKey;
    initialRef.current = serialized;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (locked) setLocked(false);
  }

  if (!locked) {
    initialRef.current = serialized;
  }

  useEffect(() => {
    if (locked) return;
    initialRef.current = serialized;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setLocked(true);
    });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!locked) return false;
  return serialized !== initialRef.current;
};
